import { ApolloClient, HttpLink, InMemoryCache, from } from "@apollo/client";
import { ErrorResponse, onError } from "@apollo/client/link/error";
import { KEEP, removeTypenameFromVariables } from "@apollo/client/link/remove-typename";

import { ApolloLink } from "@apollo/client";
import { BatchHttpLink } from "@apollo/client/link/batch-http";
import { ENVIRONMENT } from "src/js/util/environment";
import { StrictTypedTypePolicies as LiApiStrictTypedTypePolicies } from "@gql/li-api/apollo-client-helpers";
import { RetryLink } from "@apollo/client/link/retry";
import { StrictTypedTypePolicies as SitesApiStrictTypedTypePolicies } from "@gql/sites-api/apollo-client-helpers";
import { invalidateSiteLayerData } from "src/js/stores/siteGeometry/actions/invalidateSiteLayerData";
import { datadogRum } from "@datadog/browser-rum";
import liApiFragementMatcher from "@gql/li-api/fragment-matcher.json";
import { relayStylePagination } from "@apollo/client/utilities";

export enum LandTechEndpoints {
  Constraints,
  PrivateClient,
  Sites,
  Gateway,
  BatchedGateway,
  PlanningService,
  Letters,
}

const removeTypenameLink = removeTypenameFromVariables({
  except: {
    JSON: KEEP,
  },
});

const errorLink = onError(({ networkError }: ErrorResponse) => {
  if (networkError && "statusCode" in networkError) {
    if (networkError.statusCode === 401) {
      datadogRum.addAction("graphql-error-logout", {
        errorLink: "routed",
      });
      window.location.assign(ENVIRONMENT.LOGIN_URL!);
    }
  }
});

const mutationCallbackLink = new ApolloLink((operation, forward) => {
  const queryString = operation.query.loc?.source?.body || "";
  // Heuristic to detect site mutations. @todo something more robust.
  const hasSiteMutation = /mutation.*site/i.test(queryString.replaceAll("\n", ""));

  if (hasSiteMutation) {
    return forward(operation).map((result) => {
      invalidateSiteLayerData();

      return result;
    });
  }

  return forward(operation);
});

const clientRouter = new RetryLink({
  attempts: {
    retryIf: (error, operation) => {
      if (operation.getContext()?.disableRetries === true) {
        console.log("Skipping request retries");
        return false;
      }

      return !!error;
    },
  },
})
  .split(
    (operation) => {
      return operation.getContext().endpoint === LandTechEndpoints.Constraints;
    },
    new HttpLink({
      uri: `${ENVIRONMENT.CONSTRAINTS_API_URL}/graphql`,
      headers:
        ENVIRONMENT.APP_ENV === "local"
          ? {
              user_id: "something",
              features: "readConstraints",
            }
          : undefined,
    })
  )
  .split(
    (operation) => {
      return operation.getContext().endpoint === LandTechEndpoints.Gateway;
    },
    new HttpLink({
      uri: ENVIRONMENT.GRAPHQL_GATEWAY_URL,
    })
  )
  .split(
    (operation) => {
      return operation.getContext().endpoint === LandTechEndpoints.BatchedGateway;
    },
    new BatchHttpLink({
      uri: ENVIRONMENT.GRAPHQL_GATEWAY_URL,
    })
  )
  .split(
    (operation) => {
      return operation.getContext().endpoint === LandTechEndpoints.PrivateClient;
    },
    new HttpLink({
      uri: `${ENVIRONMENT.ACCOUNTS_SERVICE_PRIVATE_API_URL}/graphql`,
    })
  )
  .split(
    (operation) => {
      return operation.getContext().endpoint === LandTechEndpoints.Sites;
    },
    new HttpLink({
      uri: `${ENVIRONMENT.SITES_SERVICE_URL}/graphql`,
    })
  )
  .split(
    (operation) => {
      return operation.getContext().endpoint === LandTechEndpoints.Letters;
    },
    new HttpLink({
      uri: `${ENVIRONMENT.LETTERS_SERVICE_URL}/public/graphql`,
    })
  )
  .split(
    (operation) => {
      return operation.getContext().endpoint === LandTechEndpoints.PlanningService;
    },
    new HttpLink({
      uri: `${ENVIRONMENT.PLANNING_SERVICE_V2_URL_EXTERNAL}/graphql`,
    })
  );

const typePolicies: LiApiStrictTypedTypePolicies & SitesApiStrictTypedTypePolicies = {
  Query: {
    fields: {
      ownershipSearch: {
        keyArgs: ["search", "sort"],
        merge: true,
      },
      sites: relayStylePagination([
        "assignees",
        "includeHidden",
        "localAuthorities",
        "searchString",
        "siteIds",
        "siteLabels",
        "siteSort",
        "stageIds",
      ]),
      workspace(_, { args, toReference }) {
        return toReference({
          __typename: "Workspace",
          id: args?.id ?? "",
        });
      },
      site(_, { args, toReference }) {
        return toReference({
          __typename: "Site",
          _id: args?._id ?? "",
        });
      },
    },
  },
  SiteLabel: {
    fields: {
      name: {
        merge(existing, incoming) {
          if (incoming == null && existing != null) {
            return existing;
          }
          return incoming;
        },
      },
    },
  },
  Site: {
    fields: {
      site_labels: {
        merge(existing, incoming) {
          if (Array.isArray(incoming)) {
            return incoming;
          }
          if (existing && existing.length > 0) {
            return existing;
          }
          return incoming;
        },
      },
    },
  },
  TitleOwnership: {
    fields: {
      company: {
        merge(existing, incoming) {
          if (incoming == null && existing != null) {
            return existing;
          }
          if (incoming != null && existing == null) {
            return incoming;
          }
          if (incoming == null) {
            return existing;
          }
          return { ...existing, ...incoming };
        },
      },
    },
  },
};

const cache = new InMemoryCache({
  possibleTypes: {
    ...liApiFragementMatcher.possibleTypes,
  },
  typePolicies,
});

const LOG_FREQUENCY_MS = 1000 * 60 * 30;
function logCacheSize(cache: InMemoryCache) {
  import("@datadog/browser-rum")
    .then(({ datadogRum }) => {
      datadogRum.addAction("apollo-cache", {
        // @ts-expect-error data is marked as private but is accessible
        cacheSize: Object.keys(cache.data.data).length,
      });

      setTimeout(() => logCacheSize(cache), LOG_FREQUENCY_MS);
    })
    .catch((error) => {
      console.error("Failed to log deck.gl metrics", error);
    });
}

// trigger the first log after 1/6 of the frequency (5 minutes)
setTimeout(() => logCacheSize(cache), LOG_FREQUENCY_MS / 6);

export const routedClient = new ApolloClient({
  // Client Router or Fallback to LI
  link: from([
    mutationCallbackLink,
    removeTypenameLink,
    errorLink,
    clientRouter,
    new HttpLink({
      uri: `${ENVIRONMENT.API_URL}/graphql`,
    }),
  ]),
  cache,
});

export const routedClientWithoutTypenames = new ApolloClient({
  // Client Router or Fallback to LI
  link: from([
    mutationCallbackLink,
    removeTypenameLink,
    errorLink,
    clientRouter,
    new HttpLink({
      uri: `${ENVIRONMENT.API_URL}/graphql`,
    }),
  ]),
  cache: new InMemoryCache({ addTypename: false }),
});
